import { useState, useCallback, useRef, useEffect } from 'react';
import { isValidFlashlightColorKey } from '../constants/flashlightColors';

const MIN_INTERVAL_MS = 50;

/**
 * Throttled WebSocket sync for battle drag flashlight (cosmetic).
 * Positions are normalized to the viewport-wrapper rect so different resolutions align.
 */
export function useBattleFlashlightNetworkSync({
  enabled,
  gameId,
  viewportWrapperRef,
  getSocket,
  flashlightColor,
}) {
  const [opponentNormalized, setOpponentNormalized] = useState(null);
  const rateRef = useRef({ lastSent: 0, timer: null, pending: null });
  const flashlightColorRef = useRef(flashlightColor);

  useEffect(() => {
    flashlightColorRef.current = flashlightColor;
  }, [flashlightColor]);

  const onLocalBeamSample = useCallback(
    (sample) => {
      if (!enabled) return;
      const sock = typeof getSocket === 'function' ? getSocket() : null;
      if (!sock || sock.readyState !== 1 || !gameId) return;

      if (!sample.active) {
        if (rateRef.current.timer != null) {
          clearTimeout(rateRef.current.timer);
          rateRef.current.timer = null;
        }
        rateRef.current.pending = null;
        rateRef.current.lastSent = 0;
        sock.send(JSON.stringify({ type: 'battle-flashlight', gameId, active: false }));
        return;
      }

      const root = viewportWrapperRef.current;
      if (!root) {
        throw new Error('useBattleFlashlightNetworkSync: viewportWrapperRef.current is missing');
      }
      const rect = root.getBoundingClientRect();
      if (!(rect.width > 0) || !(rect.height > 0)) {
        throw new Error('useBattleFlashlightNetworkSync: viewport wrapper has non-positive size');
      }

      const nx = Math.min(1, Math.max(0, (sample.clientX - rect.left) / rect.width));
      const ny = Math.min(1, Math.max(0, (sample.clientY - rect.top) / rect.height));

      const colorKey = flashlightColorRef.current;
      if (!isValidFlashlightColorKey(colorKey)) {
        throw new Error(`useBattleFlashlightNetworkSync: invalid flashlightColor "${colorKey}"`);
      }

      if (rateRef.current.timer != null) {
        clearTimeout(rateRef.current.timer);
        rateRef.current.timer = null;
      }

      rateRef.current.pending = { nx, ny, color: colorKey };

      const flush = () => {
        rateRef.current.timer = null;
        const pending = rateRef.current.pending;
        if (!pending) return;
        rateRef.current.pending = null;
        const s = typeof getSocket === 'function' ? getSocket() : null;
        if (!s || s.readyState !== 1 || !gameId) return;
        s.send(
          JSON.stringify({
            type: 'battle-flashlight',
            gameId,
            active: true,
            nx: pending.nx,
            ny: pending.ny,
            color: pending.color,
          })
        );
        rateRef.current.lastSent = Date.now();
      };

      const now = Date.now();
      if (now - rateRef.current.lastSent >= MIN_INTERVAL_MS) {
        flush();
      } else {
        rateRef.current.timer = setTimeout(flush, MIN_INTERVAL_MS - (now - rateRef.current.lastSent));
      }
    },
    [enabled, gameId, viewportWrapperRef, getSocket]
  );

  useEffect(
    () => () => {
      if (rateRef.current.timer != null) {
        clearTimeout(rateRef.current.timer);
      }
    },
    []
  );

  return { opponentNormalized, setOpponentNormalized, onLocalBeamSample };
}
